/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
// Redux
import { useTypedDispatch } from "@/common/lib/redux/store";
import { loadingOn, loadingOff } from "@/common/lib/redux/actions/commonAction";
// Components
import ListDataContainer from "./ListDataContainer";
import ListPagination from "./ListPagination";
import { Theme, Typography } from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
// Styles
import { useStyles } from "@/common/lib/style/hooks";
// API
import { getUserList } from "@/common/lib/api/user";

const styles = (theme: Theme) => ({
  container: {
    padding: "45px 75px",
  },
  title: {
    minWidth: 900,
    marginBottom: 30,
    "& > div.title": {
      display: "flex",
      alignItems: "center",
      marginBottom: 10,
      "& > svg": {
        fontSize: "2.35rem",
        color: theme.palette.primary.main,
        marginRight: 10,
      },
      "& > p": {
        fontSize: "2.35rem",
        fontWeight: 500,
        color: theme.palette.primary.main,
        userSelect: "none",
      },
    },
    "& > div.content": {
      display: "flex",
      alignItems: "center",
      "&::before": {
        display: "inline-block",
        content: "''",
        width: 15,
        height: 80,
        backgroundColor: theme.palette.primary.main,
        marginRight: 15,
      },
      "& > div": {
        "& > .MuiTypography-body1": {
          fontSize: "1.15rem",
          userSelect: "none",
        },
        "& > .MuiTypography-body2": {
          fontSize: "0.975rem",
          userSelect: "none",
          "& > span > span": {
            fontSize: "1.125rem",
            fontWeight: "bold",
            color: theme.palette.primary.main,
          },
        },
        "& > .MuiTypography-caption": {
          display: "block",
          color: theme.palette.error.main,
          fontSize: "0.875rem",
          fontWeight: "bold",
          userSelect: "none",
        },
      },
    },
  },
  content: {
    width: 1473,
  },
});

const ListContainer = () => {
  const classes = useStyles(styles);
  const { t } = useTranslation();
  const dispatch = useTypedDispatch();

  const [data, setData] = useState<UserData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [params, setParams] = useState<{ [key: string]: any }>({});

  const [init, setInit] = useState<boolean>(false);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(30);
  const [pageDot, setPageDot] = useState<number>(1);

  const userListQuery = useQuery(
    "userList",
    () => {
      dispatch(loadingOn());
      let listParams = { ...params };
      return getUserList(listParams);
    },
    {
      retry: false,
      onSuccess: (response) => {
        setData(response.data.results);
        setTotalCount(response.data.count);
        setInit(true);
      },
      onSettled: () => {
        dispatch(loadingOff());
      },
    }
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setPageDot(Math.floor((page - 1) / (300 / pageSize)) + 1);
  }, [page]);

  useEffect(() => {
    if (init) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [page, pageSize, params]);

  useEffect(() => {
    if (init) {
      userListQuery.refetch();
    }
  }, [pageDot, pageSize, params]);

  return (
    <div className={classes.container}>
      <div className={classes.title}>
        <div className="title">
          <ViewListIcon />
          <Typography>{t("USER.TITLE_LIST")}</Typography>
        </div>
      </div>
      <div className={classes.content}>
        <ListDataContainer
          data={data}
          setData={setData}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          params={params}
          setParams={setParams}
        />
        <ListPagination
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={totalCount}
        />
      </div>
    </div>
  );
};

export default ListContainer;
